/**
 * Common JS controller.
 *
 * Use this class to run scripts globally and to provide
 * modular helper functions for other scripts classes.
 */

// Use jQuery as $ within this file scope.
const $ = jQuery;

/**
 * Class Common
 */
class Common {

    /**
     * Class constructor is for binding class properties.
     * No explicit constructor is required; the default constructor is used.
     */

    /**
     * Cache dom elements for use in the class's methods
     *
     * @return {void}
     */
    cache() {

        // The accessible outline style container.
        this.focusStyleContainer = document.getElementById( 'accessible-outline' );

        // Scroll to links. Used for animated internal linking.
        this.$scrollToLink = $( '.js-scroll-to' );

        // Outgoing links
        const outgoingSelector = 'a[target="_blank"]:not(.some-circle):not(.cookiebot-helper-placeholder__link)';
        this.linksInNewTab = document.querySelectorAll( outgoingSelector );

        // Links in wysiwyg content with external links
        const contentLinks = document.querySelectorAll( '.wysiwyg a:not(.cookiebot-helper-placeholder__link)' );
        this.externalLinks = [ ...contentLinks ].filter( ( link ) => {
            const hostName = link.hostname;
            if ( hostName ) {

                // Get links where hostname does not match site URL without subdomain.
                return ! hostName.toLowerCase().includes( themeData.baseDomain.toLowerCase() ); // eslint-disable-line no-undef
            }
            return false;
        } );

        this.indicatorIcon = '<i class="ion-android-open" aria-hidden="true"></i>';
        this.ariaLabelNewTab = themeData.themeStrings.common.opens_in_new_window; // eslint-disable-line no-undef
        this.ariaLabelExternal = themeData.themeStrings.common.opens_external_link; // eslint-disable-line no-undef
    }

    /**
     * All common events
     *
     * @return {void}
     */
    events() {

        // Add the outline nullifying style to the
        // container in the document head when using the mouse.
        document.body.addEventListener( 'mousedown', () => {
            this.nullifyOutline();
            document.body.classList.add( 'js-ui-is-mouse' );
            document.body.classList.add( 'js-ui-not-touch' );
            document.body.classList.remove( 'js-ui-is-touch' );
            document.body.classList.remove( 'js-ui-is-keyboard' );
        } );

        // Add the outline nullifying style to the
        // container in the document head on touchevents.
        document.body.addEventListener( 'touchstart', () => {
            this.nullifyOutline();
            document.body.classList.add( 'js-ui-is-touch' );
            document.body.classList.remove( 'js-ui-not-touch' );
            document.body.classList.remove( 'js-ui-is-mouse' );
            document.body.classList.remove( 'js-ui-is-keyboard' );
        } );

        // Remove the outline nullifying style from the
        // container in the document head when using the keyboard.
        document.body.addEventListener( 'keydown', () => {
            if ( this.focusStyleContainer ) {
                this.focusStyleContainer.innerHTML = '';
            }

            document.body.classList.add( 'js-ui-is-keyboard' );
            document.body.classList.add( 'js-ui-not-touch' );
            document.body.classList.remove( 'js-ui-is-mouse' );
            document.body.classList.remove( 'js-ui-is-touch' );
        } );

        // Scroll to element on page. Will act as normal link if no element with the given ID (href of link) exists.
        this.$scrollToLink.click( function( e ) {
            const trigger = this;

            // Get ID of element to scroll to
            const targetId = $( e.currentTarget ).attr( 'href' );
            const $targetElement = $( targetId );

            // Return early if no element with given ID exists.
            if ( ! $targetElement.length ) {
                return;
            }

            e.preventDefault();

            // Scroll to element.
            const newPosition = $targetElement.offset();
            $( 'html, body' ).stop().animate( { scrollTop: newPosition.top }, 500, function() {

                // Trigger a change to make sure the url is updated and focus changes properly.
                // @link https://codepen.io/ppscvalentin/pen/JNNBzQ
                window.location.href = trigger.href;
            } );

        } );

        // If the focused element is too low on the screen, scroll down enough to show it
        // accounting the mobile menu which is attached to the bottom of the screen
        document.addEventListener( 'focusin', () => {
            // Get the currently active/focused element and its distance from the bottom of the viewport
            const active = document.activeElement;
            if ( ! active || Common.isFixed( active ) ) {
                return;
            }
            const rect = active.getBoundingClientRect();
            const spaceBelow = window.innerHeight - rect.bottom;

            // If the element's bottom is less than 50px from the viewport bottom, scroll down
            if ( spaceBelow < 50 ) {

                window.scrollBy( 0, rect.height + 100 );
            }
        } );

        // Handle outgoing links labeling
        this.labelOutgoingLinks();
    }

    /**
     * Trigger a click event for a specific element.
     *
     * @param {Object} e  The click event.
     * @param {Object} el The captured event target element.
     * @return {void}
     * @memberof Menu
     */
    triggerClick( e, el ) {

        // Get selector for element tot click.
        const selector = el.getAttribute( 'data-selector' );
        if ( selector ) {
            $( selector ).first().trigger( 'click' );
        }
    }

    /**
     * Add nullifying outline CSS rules to focusStyleContainer.
     *
     * @return {void}
     */
    nullifyOutline() {
        if ( this.focusStyleContainer ) {
            this.focusStyleContainer.innerHTML = '*:focus{outline:none!important;}';
        }
    }

    /**
     * Fallback for object-fit (needed for IE browsers that are older than Edge 16).
     *
     * @see https://medium.com/@primozcigler/neat-trick-for-css-object-fit-fallback-on-edge-and-other-browsers-afbc53bbb2c3
     * @return {void}
     */
    objecfitFallback() {
        if ( ! Modernizr.objectfit ) { // eslint-disable-line no-undef

            // Get all object fit containers
            const containers = Common.$( '.js-object-fit-container' );

            // Loop containers nodelist with Internet Explorer compatible way to use Array.prototype.forEach for iteration.
            Array.prototype.forEach.call( containers, ( container ) => {

                // Get object fit image element
                const img = Common.$1( '.js-object-fit-img', container );

                if ( img ) {

                    // Get object fit image url
                    const imgUrl = img.getAttribute( 'src' );

                    // Add class to container
                    container.classList.add( 'js-object-fit-fallback' );

                    // Add image as bg to container
                    container.style.backgroundImage = 'url(' + imgUrl + ')';
                }
            } );
        }
    }

    /**
     * Add localized aria-label with the link text and "opens in new window" for links
     * opening in new tab, and "external link" for outgoing links.
     *
     * @return {void}
     */
    labelOutgoingLinks() {

        // Add label and icon to links opening in new tab
        this.linksInNewTab.forEach( ( link ) => {
            link.setAttribute( 'aria-label', `${ link.textContent } (${ this.ariaLabelNewTab })` ); // eslint-disable-line no-undef
            link.innerHTML += this.indicatorIcon;
        } );

        // Add label and icon to external links
        this.externalLinks.forEach( ( link ) => {
            const ariaLabel = link.getAttribute( 'aria-label' );
            if ( ariaLabel ) {

                // If link already has aria-label, only update it
                const updatedLabel = `${ link.textContent } (${ this.ariaLabelNewTab }, ${ this.ariaLabelExternal })`;
                link.setAttribute( 'aria-label', updatedLabel );
            } else {

                // Otherwise set the aria-label and add the icon
                link.setAttribute( 'aria-label', `${ link.textContent } (${ this.ariaLabelExternal })` );
                link.innerHTML += this.indicatorIcon;
            }
        } );
    }

    /**
     * Run when the document is ready.
     *
     * @return {void}
     */
    docReady() {

        this.cache();
        this.objecfitFallback();
        this.events();
    }

    /**
     * Check if all images have been loaded by the browser.
     *
     * @param {Element|Document} context  A query context DOM object. Used to find all images inside given object.
     * @param {Function}         callback Optional callback function to run when all elements have been loaded.
     * @return {void}
     * @memberof Common
     */
    static imagesLoaded( context, callback ) {

        // Get all image elements. Make sure given context is valid.
        let images;
        try {
            images = ( context ).querySelectorAll( 'img' );
        } catch ( error ) {
            console.error( error ); // eslint-disable-line
            return;
        }

        const totalImages = images.length;
        let nbrOfImagesLoaded = 0;

        // Function that runs the callback.
        const done = ( args ) => {
            if ( typeof callback === 'function' ) {
                callback( args );
            }
        };

        // Run callback and return early if no images found.
        if ( ! totalImages ) {
            done( 0 );
            return;
        }

        // Function to check if all images have been loaded or not.
        const check = () => {
            nbrOfImagesLoaded++;
            if ( nbrOfImagesLoaded === totalImages ) {
                done( nbrOfImagesLoaded );
            }
        };

        Array.from( images ).forEach( ( img ) => {

            // Img.complete is needed img.onload will not run in IE 11.
            // Because img.complete might return true (at least in FF) even before the data-srcset value has been copied
            // to srcset, we have to check that the img width is wider than of our placeholder img (1px).
            // If no support for IE11 is required, this block can probably be removed.
            // https://stackoverflow.com/a/33025364
            if ( img.complete && img.naturalWidth > 1 ) {
                check();
                return;
            }

            // This code will not run in IE 11 (Edge is fine) if image is in the cache.
            img.onload = () => {
                check();
            };

            img.onerror = () => {
                check();
            };
        } );
    }

    /**
     * Toggles the state of an elements aria-hidden attribute. If the element does not have an aria-hidden attribute,
     * one will be created and set to false.
     *
     * @param {Object} element The element to toggle
     * @return {void}
     */
    static toggleAriaHidden( element ) {
        const ariaExpandedState = element.getAttribute( 'aria-hidden' ) === 'false' ? true : false;
        element.setAttribute( 'aria-hidden', ariaExpandedState );
    }

    /**
     * Checks if the element has a specific attribute and toggles its stage.
     *
     * @param {Object} elements The nodelist containing the elements
     * @return {void}
     */

    /**
     * Checks if the element has a specific attribute and changes its value to the opposite.
     *
     * @static
     * @param {Object} element   Element to toggle
     * @param {string} attribute Attribute to toggle
     * @return {void}
     * @memberof Common
     */
    static toggleBooleanAttribute( element, attribute ) {
        const value = element.getAttribute( attribute );
        if ( value && ( value === 'false' || value === 'true' ) ) {
            const newValue = value === 'false' ? true : false;
            element.setAttribute( attribute, newValue );
        }
    }

    /**
     * Removes classname from each element in a nodelist or array of elements.
     *
     * @param {Object} elements  The nodelist containing the elements
     * @param {string} className The classlist to remove
     * @return {void}
     */
    static removeClassFromAll( elements, className ) {

        // Return early if missing required attributes.
        if ( ! elements || ! className ) {
            return;
        }

        Array.from( elements ).forEach( ( element ) => {
            element.classList.remove( className );
        } );
    }

    /**
     * Set attribute on each element in a nodelist or array of elements.
     *
     * @param {Object} elements  The nodelist or array containing the elements
     * @param {string} attribute The attribut to change
     * @param {string} value     The value of the attribute
     * @return {void}
     */
    static setAttrOnAll( elements, attribute, value ) {

        // // Return early if missing required attributes.
        if ( ! elements || ! attribute || ! value ) {
            return;
        }

        Array.from( elements ).forEach( ( element ) => {
            element.setAttribute( attribute, value );
        } );
    }

    /**
     * Get all the siblings of an element. Optionally specify a classname to only get the elements with said class.
     *
     * @param {Object} element   The element whose sibilings to find.
     * @param {string} classname Optional classname that siblings must match.
     * @return {Object}    Returns empty array if no matches are found; otherwise, it returns all matching elements.
     */
    static siblings( element, classname ) {

        // Return empty arry if missing required attributes
        if ( ! element ) {
            return [];
        }

        return Array.prototype.filter.call( element.parentNode.children,
            function( child ) {
                if ( classname ) {
                    if ( ! child.classList.contains( classname ) ) {
                        return false;
                    }
                }
                return child !== element;
            }
        );
    }

    /**
     * Select a list of matching child elements from an array of elements.
     *
     * @param {NodeList|Array} elements A nodelist or array; use each item in nodelist as context when finding the children.
     * @param {string}         selector The query selector string.
     * @return {Element[]} Returns empty array if no matches are found; otherwise, it returns all matching elements.
     */
    static childrenFromEach( elements, selector ) {

        // Return empty array if missing required attributes or elements empty
        if ( ! elements || ! selector || ! elements.length > 0 ) {
            return [];
        }

        const arrayOfNodes = Array.prototype.map.call( elements,
            function( element ) {

                // Get all matching elements.
                const matchingChildren = ( element ).querySelectorAll( selector );

                // Convert nodelist to array.
                return [ ...matchingChildren ];
            }
        );

        // Flatten the array
        // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/Reduce?v=a
        const flattened = arrayOfNodes.reduce( ( a, b ) => [ ...a, ...b ] );
        return flattened;
    }

    /**
     * Safely stops a JS event.
     *
     * @param {Event} e Event object.
     * @return {void}
     */
    static stop( e ) {
        if ( e && typeof e.preventDefault === 'function' ) {
            e.preventDefault();
        }
    }

    /**
     * Select a list of matching elements, context is optional.
     *
     * @param {string} selector The query selector string.
     * @param {Object} context  A query context object.
     * @return {Object}          Returns empty nodelist if no matches are found; otherwise, it returns a nodelist of matching elements.
     */
    static $( selector, context ) {
        return ( context || document ).querySelectorAll( selector );
    }

    /**
     * Select the first match only, context is optional.
     *
     * @param {string} selector The query selector string.
     * @param {Object} context  A query context object.
     * @return {object|null}     Returns null if no matches are found; otherwise, it returns the first matching element.
     */
    static $1( selector, context ) {
        return ( context || document ).querySelector( selector );
    }

    /**
     * Check if an element is positioned fixed.
     *
     * @param {Element|null} element Element to test for fixed positioning.
     * @return {boolean} True if the element is fixed; otherwise false.
     */
    static isFixed( element ) {
        if ( ! element || element.nodeType !== 1 ) {
            return false;
        }
        try {
            return window.getComputedStyle( element ).position === 'fixed';
        }
        catch ( err ) {
            return false;
        }
    }

}

// Export the class reference.
module.exports = Common;



// WEBPACK FOOTER //
// ./assets/scripts/common.js